import { createSignal } from "solid-js";
import {
  CalendarMonth,
  toISO,
  todayISO,
  tryParseISO,
  type CalendarDate,
} from "~/common/date_picker";
import { makeInputClass } from "~/common/input";
import { Popover, PopoverContent } from "~/common/popover";
import { Icon } from "~/icon";
import { formatWeekdayDayMonthDate } from "~/util/format";
import clsx from "~/util/html/style";

// A day as YYYY-MM-DD, the same value a native date input carries, picked from the app's calendar.
// Replaces the browser's own date picker, which looks different in every browser.
export function DateInput(props: {
  id?: string;
  /** YYYY-MM-DD, or "" for no day. */
  value: string;
  onChange: (value: string) => void;
  /** The last day that can be picked (YYYY-MM-DD), and why later ones can't be. */
  max?: string;
  maxReason?: string;
  /** The first day that can be picked (YYYY-MM-DD). */
  min?: string;
  placeholder?: string;
  width?: "full" | "fit";
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const shown = () => tryParseISO(props.value) ?? tryParseISO(todayISO())!;
  const [year, setYear] = createSignal(shown().year);
  const [month, setMonth] = createSignal(shown().month);
  const isDateDisabled = (date: CalendarDate) =>
    Boolean((props.max && toISO(date) > props.max) || (props.min && toISO(date) < props.min));

  return (
    <Popover
      placement="bottom-start"
      renderTrigger={(trigger) => (
        <button
          ref={trigger.props.ref}
          type="button"
          id={props.id}
          aria-describedby={props["aria-describedby"]}
          aria-invalid={props["aria-invalid"]}
          class={clsx(
            makeInputClass(),
            "flex h-input cursor-pointer items-center gap-2 text-left text-base",
            props.width !== "fit" && "w-full",
          )}
          onClick={() => {
            // Open on the month holding the value.
            setYear(shown().year);
            setMonth(shown().month);
            trigger.setOpen((open) => !open);
          }}
        >
          <Icon name="calendar" size="sm" class="text-typography-secondary" />
          <span class={clsx(!props.value && "text-typography-tertiary")}>
            {props.value
              ? formatWeekdayDayMonthDate(props.value)
              : (props.placeholder ?? "Pick a day")}
          </span>
        </button>
      )}
    >
      {(content) => (
        <PopoverContent class="p-3">
          <CalendarMonth
            year={year()}
            month={month()}
            onSelectDate={(date) => {
              props.onChange(toISO(date));
              content.setOpen(false);
            }}
            onSelectMonth={setMonth}
            onSelectYear={setYear}
            onPrev={() => {
              if (month() === 0) {
                setMonth(11);
                setYear(year() - 1);
              } else setMonth(month() - 1);
            }}
            onNext={() => {
              if (month() === 11) {
                setMonth(0);
                setYear(year() + 1);
              } else setMonth(month() + 1);
            }}
            isMarked={(date) => toISO(date) === props.value}
            isDateDisabled={isDateDisabled}
            disabledReason={props.maxReason}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}
